import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Switch,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconMail } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CompanySecurityPolicyResponse,
  CompanySecurityPolicyPayload,
  DEFAULT_PASSWORD_RULES,
  getCompanySecurityPolicy,
  parseApiErrorCode,
  PasswordRules,
  PolicySource,
  putCompanySecurityPolicy,
} from '@mygpt/app';
import { PasswordRulesFields } from '@mygpt/app/security';
import ContainerHeader from '@/components/Headers/ContainerHeader';

/**
 * 企业管理端 · 安全策略（契约 §5）
 * MFA 与密码规则各自独立的「继承全局默认 / 自定义覆盖」，PUT 两个 source 必传
 * 页面数据访问由后端鉴权：非企业管理员 403 时全局拦截器会跳转 /no-access（菜单可见性由 hasAdminConsoleAuthority 控制）
 */
const SecurityPolicyPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<CompanySecurityPolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 编辑态：MFA 与密码规则的 source 各自独立
  const [mfaSource, setMfaSource] = useState<PolicySource>('inherit');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [ruleSource, setRuleSource] = useState<PolicySource>('inherit');
  const [rules, setRules] = useState<PasswordRules>(DEFAULT_PASSWORD_RULES);

  const seedForm = (res: CompanySecurityPolicyResponse) => {
    setMfaSource(res.mfa.source);
    setMfaEnabled(
      res.mfa.source === 'custom'
        ? res.mfa.custom?.mfa_enabled ?? res.mfa.effective.mfa_enabled
        : res.mfa.effective.mfa_enabled
    );
    setRuleSource(res.password_rules.source);
    setRules(
      res.password_rules.source === 'custom'
        ? res.password_rules.custom ?? res.password_rules.effective ?? DEFAULT_PASSWORD_RULES
        : res.password_rules.effective ?? res.password_rules.global ?? DEFAULT_PASSWORD_RULES
    );
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getCompanySecurityPolicy();
        if (res.data) {
          setData(res.data);
          seedForm(res.data);
        }
      } catch (error: any) {
        notifications.show({ message: error?.errorMsg || t('security.errors.load_failed'), color: 'red' });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 密码规则联动用的「生效 MFA」：自定义时取编辑值，继承时取全局生效值 */
  const effectiveMfaForRules = mfaSource === 'custom' ? mfaEnabled : data?.mfa.effective.mfa_enabled ?? false;

  /** MFA 前置变化时联动关闭「无期限」（契约 NO_EXPIRY_PREREQUISITE 前置） */
  const syncNoExpiry = (nextEffectiveMfa: boolean) => {
    setRules((r) => (r.no_expiry && !(nextEffectiveMfa && r.rotate_90d) ? { ...r, no_expiry: false } : r));
  };

  const handleMfaToggle = (checked: boolean) => {
    setMfaEnabled(checked);
    syncNoExpiry(checked);
  };

  const handleMfaSourceChange = (value: string) => {
    const source = value as PolicySource;
    setMfaSource(source);
    if (source === 'custom') {
      // 切到自定义：以 custom（已有覆盖值）?? global（null 则 effective，再 null 用界面默认 false）为编辑初值
      const init = data?.mfa.custom ?? data?.mfa.global ?? data?.mfa.effective ?? null;
      const next = init?.mfa_enabled ?? false;
      setMfaEnabled(next);
      syncNoExpiry(next);
    } else {
      syncNoExpiry(data?.mfa.effective.mfa_enabled ?? false);
    }
  };

  const handleRuleSourceChange = (value: string) => {
    const source = value as PolicySource;
    setRuleSource(source);
    if (source === 'custom') {
      // 切到自定义：以 custom ?? global ?? effective ?? 界面默认为编辑初值
      const init =
        data?.password_rules.custom ??
        data?.password_rules.global ??
        data?.password_rules.effective ??
        DEFAULT_PASSWORD_RULES;
      const allowed = effectiveMfaForRules && init.rotate_90d;
      setRules(!init.no_expiry || allowed ? init : { ...init, no_expiry: false });
    } else if (data) {
      // 回到继承：展示全局生效值
      setRules(data.password_rules.effective ?? data.password_rules.global ?? DEFAULT_PASSWORD_RULES);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 两个 source 必传；对应 source=custom 时才传值（契约 §5.2）
      const payload: CompanySecurityPolicyPayload = {
        mfa_source: mfaSource,
        rule_source: ruleSource,
        ...(mfaSource === 'custom' ? { mfa_enabled: mfaEnabled, mfa_methods: ['email'] } : {}),
        password_rules: ruleSource === 'custom' ? rules : null,
      };
      const res = await putCompanySecurityPolicy(payload);
      // 200 返回保存后的 GET 同构响应，直接用它刷新页面
      if (res.data) {
        setData(res.data);
        seedForm(res.data);
      }
      notifications.show({ message: t('security.save_success'), color: 'green' });
    } catch (error: any) {
      const codeDetail = parseApiErrorCode(error);
      if (codeDetail?.code === 'NO_EXPIRY_PREREQUISITE') {
        notifications.show({ message: t('security.errors.no_expiry_prerequisite'), color: 'red' });
      } else if (codeDetail?.code === 'INVALID_RULES') {
        notifications.show({
          message: t('security.errors.invalid_rules', { message: codeDetail.message || '' }),
          color: 'red',
        });
      } else {
        notifications.show({ message: error?.errorMsg || t('security.errors.save_failed'), color: 'red' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (data) {
      seedForm(data);
    }
  };

  const sourceSegmentData = [
    { value: 'inherit', label: t('security_policy.source_inherit') },
    { value: 'custom', label: t('security_policy.source_custom') },
  ];

  const sourceBadge = (source: PolicySource) => (
    <Badge variant="light" color={source === 'custom' ? 'violet' : 'gray'}>
      {source === 'custom' ? t('security_policy.badge_custom') : t('security_policy.badge_inherit')}
    </Badge>
  );

  // 继承态展示 MFA 生效值（灰显只读）
  const mfaDisplayChecked = mfaSource === 'custom' ? mfaEnabled : data?.mfa.effective.mfa_enabled ?? false;

  return (
    <>
      <ContainerHeader title={t('security_policy.title')} description={t('security_policy.description')} />

      {loading ? (
        <Stack gap="md">
          <Skeleton height={180} />
          <Skeleton height={320} />
        </Stack>
      ) : (
        <Stack gap="lg" pb={40} maw={800}>
          {/* MFA 卡片 */}
          <Paper shadow="xs" p="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600}>{t('security_policy.mfa_section')}</Text>
                {sourceBadge(mfaSource)}
              </Group>

              <div>
                <Text size="sm" c="dimmed" mb={6}>
                  {t('security_policy.source_label')}
                </Text>
                <SegmentedControl value={mfaSource} onChange={handleMfaSourceChange} data={sourceSegmentData} />
              </div>

              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div>
                  <Text size="sm" fw={500}>
                    {t('security_policy.company_mfa')}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    {t('security_policy.company_mfa_desc')}
                  </Text>
                </div>
                <Switch
                  checked={mfaDisplayChecked}
                  onChange={(e) => handleMfaToggle(e.currentTarget.checked)}
                  disabled={mfaSource === 'inherit'}
                  size="md"
                />
              </Group>

              {/* 验证方式：本期仅邮箱验证码（契约 §0.4） */}
              <Paper p="sm" radius="md" withBorder className="bg-gray-50">
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="light" color="violet" size="lg" radius="md">
                    <IconMail size={18} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={500}>
                      {t('security.method_email')}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Stack>
          </Paper>

          {/* 密码规则卡片 */}
          <Paper shadow="xs" p="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600}>{t('security_policy.rules_section')}</Text>
                {sourceBadge(ruleSource)}
              </Group>

              <div>
                <Text size="sm" c="dimmed" mb={6}>
                  {t('security_policy.source_label')}
                </Text>
                <SegmentedControl value={ruleSource} onChange={handleRuleSourceChange} data={sourceSegmentData} />
              </div>

              {/* 全局未配置且未自定义时按 legacy 行为生效 */}
              {ruleSource === 'inherit' && data?.password_rules.effective_legacy && (
                <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
                  {t('security_policy.legacy_hint')}
                </Alert>
              )}

              <PasswordRulesFields
                value={rules}
                onChange={setRules}
                disabled={ruleSource === 'inherit'}
                mfaEnabled={effectiveMfaForRules}
              />

              {ruleSource === 'inherit' && (
                <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
                  {t('security_policy.inherit_hint')}
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* 操作按钮 */}
          <Group>
            <Button onClick={handleSave} loading={saving} disabled={!data}>
              {t('security_policy.save')}
            </Button>
            <Button variant="default" onClick={handleCancel} disabled={saving}>
              {t('security_policy.cancel')}
            </Button>
          </Group>
        </Stack>
      )}
    </>
  );
};

export default SecurityPolicyPage;
